import { getPortalBaseUrl } from '../js/constants/constants';
import { Tag } from '../library/types/Tags';
import { ListCollection, PagingRequest } from '../types/List';
import { InvitedUser, User, UserInvitationPayload, UserUpdatePayload, UserUserGroup } from '../types/User';
import { Folder } from '../types/Folder';
import { getEtag, setEtag } from './etag.service';
import { fetchData } from './fetch-service';
import { getAccessToken } from './token.service';

const PortalBaseUrl = getPortalBaseUrl()

export const getUsers = async (options: PagingRequest): Promise<ListCollection<User>> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/users${options ? `?page=${options.page}&perpage=${options.perpage}` : ''}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const getInvitedUsers = async (options: PagingRequest): Promise<ListCollection<InvitedUser>> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(
        `${PortalBaseUrl}/api/users/invitations${options ? `?page=${options.page}&perpage=${options.perpage}` : ''}`,
        {
            headers: { Authorization: `Bearer ${accessToken}` }
        }
    );

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const getMe = async (): Promise<User> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/users/me`, { headers: { Authorization: `Bearer ${accessToken}` } });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json() as Promise<User>;
};

export const getUser = async (key: string): Promise<User> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const path = `${PortalBaseUrl}/api/users/${key}`;
    const response = await fetchData(path, { headers: { Authorization: `Bearer ${accessToken}` } });

    if (!response.ok) {
        return Promise.reject(response);
    }

    const user = (await response.json()) as User;

    setEtag(key, response.headers.get('etag') ?? '');

    return user;
};

export const updateUser = async (body: UserUpdatePayload, key: string) => {
    const etag = getEtag(key);
    const accessToken = await getAccessToken();

    const path = `${PortalBaseUrl}/api/users/${key}`;
    const response = await fetchData(path, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, etag },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    setEtag(key, response.headers.get('etag') ?? '');

    return Promise.resolve(true);
};

export const deleteUser = async (key: string): Promise<void> => {
    const accessToken = await getAccessToken();

    const path = `${PortalBaseUrl}/api/users/${key}`;
    const response = await fetchData(path, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }
};

export const resetUserPassword = async (key: string): Promise<string> => {
    const accessToken = await getAccessToken();

    const path = `${PortalBaseUrl}/api/users/${key}/resetpassword`;

    const response = await fetchData(path, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    const { password } = (await response.json()) as { password: string };

    return password;
};

export const createUserInvitations = async (body: UserInvitationPayload[]): Promise<void> => {
    const accessToken = await getAccessToken();

    const path = `${PortalBaseUrl}/api/users/invitations`;
    const response = await fetchData(path, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        return Promise.reject(response);
    }
};

export const resendUserInvitation = async (key: string): Promise<void> => {
    const accessToken = await getAccessToken();

    const response = await fetchData(`${PortalBaseUrl}/api/users/invitations/${key}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }
};

export const deleteUserInvitation = async (key: string): Promise<void> => {
    const accessToken = await getAccessToken();

    const path = `${PortalBaseUrl}/api/users/invitations/${key}`;
    const response = await fetchData(path, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }
};

export const getUserTags = async (userKey: string): Promise<Tag[]> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/users/${userKey}/tags`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const updateUserTags = async (userKey: string, tags: Tag[]): Promise<void> => {
    const accessToken = await getAccessToken();
    const etag = getEtag(userKey);

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/users/${userKey}/tags`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}`, etag },
        body: JSON.stringify(tags)
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    setEtag(userKey, response.headers.get('etag') ?? '');
};

export const getUserGroupsForUser = async (userKey: string): Promise<ListCollection<UserUserGroup>> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/users/${userKey}/usergroups`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const getFoldersForUser = async (userKey: string): Promise<ListCollection<Folder>> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/users/${userKey}/folders`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};
